import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API || 'http://localhost:8000';

function fmt(d) {
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? '—' : dt.toLocaleString();
}

function badgeStyle(status) {
  const base = {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 999,
    border: '1px solid',
    fontSize: 10,
    fontWeight: 700
  };
  if (status === 'success') return { ...base, background: '#f0fdf4', borderColor: '#22c55e', color: '#15803d' };
  if (status === 'attempt') return { ...base, background: '#eff6ff', borderColor: '#60a5fa', color: '#1d4ed8' };
  if (status === 'failed') return { ...base, background: '#fef2f2', borderColor: '#fca5a5', color: '#991b1b' };
  return { ...base, background: '#f3f4f6', borderColor: '#e5e7eb', color: '#374151' };
}

export default function AuditLogDrawer(props) {
  const { isOpen, onClose, initialDocId = null, initialFilters = {} } = props;

  const [filters, setFilters] = useState({
    docId: initialDocId || '',
    action: initialFilters.action || '',
    status: initialFilters.status || '',
  });
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const pollTimerRef = useRef(null);

  const queryParams = useMemo(() => {
    const p = { limit: 50 };
    if (filters.docId) p.docId = filters.docId;
    if (filters.action) p.action = filters.action;
    if (filters.status) p.status = filters.status;
    if (cursor) p.cursor = cursor;
    return p;
  }, [filters, cursor]);

  function reset() {
    setItems([]);
    setCursor(null);
    setHasMore(true);
  }

  async function load(append) {
    if (loading || !isOpen || !hasMore) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/audit-events`, {
        params: queryParams,
        withCredentials: true,
        headers: { 'Cache-Control': 'no-cache' }
      });
      const rows = res.data?.items || res.data?.events || res.data || [];
      const nextCursor = res.data?.nextCursor || res.data?.cursor || null;

      setItems(prev => (append ? [...prev, ...rows] : rows));
      setCursor(nextCursor);
      setHasMore(Boolean(nextCursor));
    } catch (e) {
      console.error('[AuditLogDrawer] fetch failed:', e?.message || e);
    } finally {
      setLoading(false);
    }
  }

  // Initial + when filters change
  useEffect(() => {
    if (!isOpen) return;
    reset();
    load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, filters.docId, filters.action, filters.status]);

  // Light polling every 10s for freshness while open (top refresh only)
  useEffect(() => {
    if (!isOpen) return;
    const tick = async () => {
      try {
        const res = await axios.get(`${API_URL}/admin/audit-events`, {
          params: { ...filters, limit: 25 },
          withCredentials: true
        });
        const rows = res.data?.items || res.data?.events || [];
        if (rows.length) {
          const newest = new Set(rows.map(r => r._id));
          setItems(prev => {
            const dedup = prev.filter(r => !newest.has(r._id));
            return [...rows, ...dedup];
          });
        }
      } catch (_) {}
    };
    pollTimerRef.current = setInterval(tick, 10000);
    return () => clearInterval(pollTimerRef.current);
  }, [isOpen, filters]);

  if (!isOpen) return null;

  return (
    <div style={styles.backdrop} onClick={onClose}>
      <div style={styles.drawer} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <strong>Audit Log</strong>
          <button onClick={onClose} style={styles.xBtn}>Close</button>
</div>

        <div style={styles.filterRow}>
          <input
            placeholder="Filter by docId"
            value={filters.docId}
            onChange={(e) => setFilters((f) => ({ ...f, docId: e.target.value }))}
            style={styles.input}
          />
          <input
            placeholder="Filter by action (e.g. step.queue)"
            value={filters.action}
            onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value }))}
            style={styles.input}
          />
          <select
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
            style={styles.input}
          >
            <option value="">Any status</option>
            <option value="attempt">attempt</option>
            <option value="success">success</option>
            <option value="failed">failed</option>
          </select>
          <button onClick={reset} style={styles.btn}>Clear</button>
        </div>

        <div style={styles.listBox}>
          {items.length === 0 && !loading && (
            <div style={{ color: '#6b7280', fontSize: 13 }}>No audit events.</div>
          )}

          {items.map((ev) => (
            <div key={ev._id} style={styles.row}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={badgeStyle(ev.status)}>{ev.status}</span>
                <code style={{ fontSize: 11 }}>{ev.action}</code>
                {ev.stepId && (
                  <span style={styles.pill}>step: {ev.stepId}</span>
                )}
              </div>
              <div style={styles.meta}>
                <span>doc: <code>{ev.docId || '—'}</code></span>
                <span>admin: <code title={ev.adminEmail}>{(ev.adminId || '').slice(0, 8)}…</code></span>
                <span>{fmt(ev.createdAt || ev.ts)}</span>
              </div>
              {ev.message && (
                <div style={styles.msg}>⚠ {ev.message}</div>
              )}
            </div>
          ))}

          {hasMore && (
            <div style={{ marginTop: 10 }}>
              <button onClick={() => load(true)} disabled={loading} style={styles.btn}>
                {loading ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  backdrop: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)',
    display: 'flex', justifyContent: 'flex-end', zIndex: 50
  },
  drawer: {
    width: 'min(560px, 95vw)', height: '100%', background: '#fff',
    borderLeft: '1px solid #e5e7eb', padding: 16, overflow: 'auto'
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  xBtn: { background: 'transparent', border: 'none', color: '#6b7280', cursor: 'pointer' },
  filterRow: { display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' },
  input: { border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 8px', minWidth: 160 },
  btn: { background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 10px', cursor: 'pointer' },
  listBox: { border: '1px solid #e5e7eb', borderRadius: 8, padding: 8, background: '#f8fafc' },
  row: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, padding: 8, marginBottom: 8 },
  meta: { display: 'flex', gap: 12, color: '#6b7280', fontSize: 11, marginTop: 4, flexWrap: 'wrap' },
  msg: { fontSize: 12, color: '#991b1b', marginTop: 4, background: '#fef2f2', border: '1px solid #fecaca', padding: 6, borderRadius: 6 },
  pill: {
    display: 'inline-block', padding: '2px 8px', borderRadius: 999,
    background: '#eef2ff', border: '1px solid #c7d2fe', color: '#3730a3', fontSize: 10
  }
};
